import { HttpClient } from '@angular/common/http';
import { Component } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from 'app/service/user.service';
import { MIN_PASSWORD_LENGTH, translatePasswordErrors } from 'app/validator/password-rules';
import { ConfirmPasswordValidator } from 'app/validator/confirm-password.validator';
import { ToastrService } from 'ngx-toastr';
import { Location } from '@angular/common';

@Component({
  standalone: false,
  selector: 'update-password',
  templateUrl: './update-password.component.html',
  styleUrls: ['./update-password.component.scss']
})
export class UpdatePasswordComponent {
  readonly currentYear = new Date().getFullYear();
  changePasswordForm!: FormGroup;
  submitted: boolean = false;
  readonly minLength = MIN_PASSWORD_LENGTH;
  errormsg: any;
  serverErrors: string[] = [];
  selectedLanguage: any = "en";
  oldPasswordVisible: boolean = false;
  newPasswordVisible: boolean = false;
  confirmPasswordVisible: boolean = false;
  
  supportedLanguages: { code: string, name: string }[] = [
    { code: 'de', name: 'Deutsch' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'pt', name: 'Português' }
  ];

  constructor(
    private http: HttpClient,
    public _router: Router,
    private userService: UserService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private formBuilder: FormBuilder,
    private location: Location
  ) {
    translate.setDefaultLang('en');
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage) {
      this.selectedLanguage = savedLanguage;
      translate.use(savedLanguage);
    } else {
      translate.use('en');
    }
  }

  ngOnInit() {
    this.changePasswordForm = this.formBuilder.group(
      {
        old_password: ['', [Validators.required]],
        new_password: ['', [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH)]],
        confirm_password: ['', [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH)]]
      },
      {
        validator: ConfirmPasswordValidator('new_password', 'confirm_password')
      }
    );

    const storedLanguage = localStorage.getItem('selectedLanguage');
    if (storedLanguage) {
      this.selectedLanguage = storedLanguage;
    } else {
      this.selectedLanguage = 'en';
      localStorage.setItem('selectedLanguage', 'en');
    }
    this.translate.use(this.selectedLanguage);
  }

  oldPasswordVisibility() {
    this.oldPasswordVisible = !this.oldPasswordVisible;
  }

  newPasswordVisibility() {
    this.newPasswordVisible = !this.newPasswordVisible;
  }

  confirmPasswordVisibility() {
    this.confirmPasswordVisible = !this.confirmPasswordVisible;
  }

  changeLanguage(lang: string) {
    this.selectedLanguage = lang;
    this.translate.use(lang);
    localStorage.setItem('selectedLanguage', lang);
    this.userService.loadLang.next(lang);
  }

  get f(): { [key: string]: AbstractControl } {
    return this.changePasswordForm.controls;
  }

  updatepassword() {
    this.submitted = true;
    this.errormsg = null;
    this.serverErrors = [];

    if (this.changePasswordForm.invalid) {
      return;
    }

    const data = {
      old_password: this.changePasswordForm.value.old_password,
      new_password: this.changePasswordForm.value.new_password
    };

    this.userService.updatepassword(data).subscribe(
      (res) => {
        this.toastr.success(this.translate.instant('authPages.updated'));
        this._router.navigate(['/login']);
      },
      (err) => {
        if (err.status === 400) {
          if (Array.isArray(err.error?.new_password)) {
            // Passwortregeln des Servers (z. B. zu häufig, zu ähnlich)
            this.serverErrors = translatePasswordErrors(this.translate, err.error.new_password, err.error.new_password_codes);
            return;
          }
          if (err.error && typeof err.error === 'string') {
            this.errormsg = err.error;
          } else if (err.error && err.error.error) {
            this.errormsg = err.error.error;
          } else {
            this.errormsg = 'authPages.wrongOld';
          }
          this.toastr.error(this.translate.instant(this.errormsg));
        } else if (err.status === 401) {
          this.errormsg = 'authPages.sessionExpired';
          this.toastr.error(this.translate.instant(this.errormsg));
          this._router.navigate(['/login']);
        } else {
          this.errormsg = 'authPages.generic';
          this.toastr.error(this.translate.instant(this.errormsg));
        }
      }
    );
  }

  goBack(): void {
    this.location.back();
  }
}